import { Link, useLocation } from "react-router-dom";
import {
  DashboardOutlined,
  SettingOutlined,
  UserOutlined,
  TeamOutlined,
  FileDoneOutlined,
  FileProtectOutlined,
  FileTextOutlined,
  LogoutOutlined,
  AuditOutlined,
  DollarOutlined,
  CrownOutlined,
  AppstoreOutlined,
  RobotOutlined,
  DatabaseOutlined,
  LineChartOutlined,
  DollarCircleOutlined,
  BankOutlined,
  ScanOutlined,
  DownOutlined,
  RightOutlined,
  ShoppingCartOutlined,
  ControlOutlined,
  ApartmentOutlined,
  UploadOutlined,
  PieChartOutlined,
  GlobalOutlined,
} from "@ant-design/icons";
import logoShort from "../../assets/logoShort.png";
import "./AdminSidebar.css";
import { useContext, useState } from "react";
import { ThemeContext } from "../../context/ThemeContext";
import { clearAuthData } from "../../utils/roleUtils";
import { useUser } from "../../context/UserContext";
import { useNavigate } from "react-router-dom";
import apiClient from "../../services/apiClient";

const adminNavItems = [
  { name: "Dashboard", path: "/admin", icon: <DashboardOutlined /> },
  { name: "User Management", path: "/admin/users", icon: <UserOutlined /> },
  { name: "Role Management", path: "/admin/roles", icon: <TeamOutlined /> },
  { name: "Payments", path: "/admin/payments", icon: <DollarOutlined /> },
  { name: "Orders", path: "/admin/orders", icon: <ShoppingCartOutlined /> },
  { name: "Subscriptions", path: "/admin/subscriptions", icon: <CrownOutlined /> },
  { name: "Plans Management", path: "/admin/plans", icon: <AppstoreOutlined /> },
  { name: "Sector & Industry Classification", path: "/admin/sector-industry-classification", icon: <ApartmentOutlined /> },
  // { name: "Shareholding & promoter scores", path: "/admin/shareholding-pattern", icon: <PieChartOutlined /> },
  { name: "ESG Rating List", path: "/admin/issuer-esg-ratings", icon: <GlobalOutlined /> },

  { name: "Audit Logs", path: "/admin/audit", icon: <ControlOutlined /> },
  {
    name: "System Settings",
    path: "/admin/settings",
    icon: <SettingOutlined />,
  },
  { name: "System Logs", path: "/admin/logs", icon: <FileTextOutlined /> },
  {
    name: "Approval Dashboard",
    path: "/admin/approval",
    icon: <FileDoneOutlined />,
  },
  {
    name: "News Approval",
    path: "/admin/approval/news",
    icon: <FileProtectOutlined />,
  },

  { name: "ISINs List", path: "/admin/isin-list", icon: <FileDoneOutlined /> },
  { name: "Issuer List", path: "/admin/issuer-list", icon: <FileProtectOutlined /> },
  { name: "Issuer Financial Reports", path: "/admin/issuer-financial-reports", icon: <LineChartOutlined /> },
  { name: "Issuer Borrowing", path: "/admin/issuer-borrowing", icon: <BankOutlined /> },
  { name: "Manage Cashflows", path: "/admin/manage-cashflows", icon: <DollarCircleOutlined /> },
  { name: "Manage Termsheets", path: "/admin/manage-termsheets", icon: <FileTextOutlined /> },
  { name: "Credit Risk", path: "/admin/credit-risk", icon: <RobotOutlined /> },
  // { name: "NSDL Home", path: "/admin/nsdl", icon: <FileTextOutlined /> },
];

const botsSubItems = [
  { name: "Data Fetching", path: "/admin/data-fetching", icon: <DatabaseOutlined /> },
  { name: "News Management", path: "/admin/news-management", icon: <FileTextOutlined /> },
  { name: "Market Data", path: "/admin/market-data", icon: <LineChartOutlined /> },
  { name: "Financial Data", path: "/admin/financial-data", icon: <DollarCircleOutlined /> },
  { name: "Government & NSDL", path: "/admin/government-nsdl", icon: <BankOutlined /> },
  { name: "SCAN Data", path: "/admin/scan-data", icon: <ScanOutlined /> },
];

const AdminSidebar = ({ collapsed = false }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { isDark } = useContext(ThemeContext); // force re-render on theme change
  const { clearUserData } = useUser();
  const [botsExpanded, setBotsExpanded] = useState(false);

  const isActive = (path) => {
    if (path === "/admin/users") {
      return location.pathname.startsWith("/admin/users");
    }
    if (path === "/admin/isin-list") {
      return location.pathname.startsWith("/admin/isin");
    }
    if (path === "/admin/issuer-list") {
      return location.pathname.startsWith("/admin/issuer");
    }
    return location.pathname === path;
  };

  const isBotsActive = () => {
    return botsSubItems.some(item => location.pathname.startsWith(item.path));
  };

  // console.log("AdminSidebar render - isDark:", isDark);

  return (
    <div className={`admin-sidebar ${collapsed ? "collapsed" : ""}`}>
      {/* Sidebar Header */}
      <div className="admin-sidebar-header">
        <div className="admin-logo-container">
          <img src={logoShort} alt="SimplyBond" className="admin-logo" />
          {!collapsed && <span className="admin-logo-text">Admin</span>}
        </div>
      </div>
      {/* Navigation */}
      <nav className="admin-sidebar-nav">
        {adminNavItems.map((item, idx) => (
          <Link
            key={idx}
            to={item.path}
            className={`admin-nav-item ${isActive(item.path) ? "active" : ""}`}
            title={collapsed ? item.name : undefined}
          >
            <span className="admin-nav-icon">{item.icon}</span>
            {!collapsed && <span className="admin-nav-text">{item.name}</span>}
          </Link>
        ))}

        {/* Bots Section */}
        <div className="admin-nav-section">
          <div
            className={`admin-nav-item admin-nav-collapsible ${isBotsActive() ? "active" : ""}`}
            onClick={() => setBotsExpanded(!botsExpanded)}
            title={collapsed ? "Bots" : undefined}
          >
            <span className="admin-nav-icon"><RobotOutlined /></span>
            {!collapsed && <span className="admin-nav-text">Bots</span>}
            {!collapsed && (
              <span className="admin-nav-arrow">
                {botsExpanded ? <DownOutlined /> : <RightOutlined />}
              </span>
            )}
          </div>

          {botsExpanded && !collapsed && (
            <div className="admin-nav-submenu">
              {botsSubItems.map((item, idx) => (
                <Link
                  key={idx}
                  to={item.path}
                  className={`admin-nav-subitem ${isActive(item.path) ? "active" : ""}`}
                  title={collapsed ? item.name : undefined}
                >
                  <span className="admin-nav-subicon">{item.icon}</span>
                  <span className="admin-nav-subtext">{item.name}</span>
                </Link>
              ))}
            </div>
          )}
        </div>
      </nav>
      {/* Footer */}
      <div className="admin-sidebar-footer">
        <button
          className="admin-logout-btn"
          onClick={() => {
            (async () => {
              try {
                const baseUrl = process.env.REACT_APP_AUTH_URL || "";
                await apiClient.post(`${baseUrl}/user/logout`);
              } catch (e) {
                // Ignore errors, proceed with client-side cleanup
              } finally {
                clearAuthData(navigate, clearUserData);
              }
            })();
          }}
          title={collapsed ? "Logout" : undefined}
        >
          <LogoutOutlined className="admin-logout-icon" />
          {!collapsed && <span>Logout</span>}
        </button>
      </div>
    </div>
  );
};

export default AdminSidebar;
